// shared/editable-text.directive.ts
import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { EditorService } from '../../services/editor.service';
import { InlineImageEditorComponent } from '@modules/editor/pages/inline-edit-image/inline-image-editor.component';
import { HTMLElementModel, UploadPhoto } from '../models/content.model';
import { ContentManagerService } from '../../services/content-manager.service';
import {
  EditableImageWrapperComponent
} from '@modules/editor/pages/inline-edit-image/inline-image-editor-wrapper/inline-image-editor.component';

@Directive({
  selector: '[appEditableImage]',
  standalone: true,
})
export class EditableImageDirective implements OnDestroy {
  @Output() appEditableImageSave = new EventEmitter<HTMLElement>();

  private editorRef: ComponentRef<InlineImageEditorComponent> | null = null;
  private currentEl!: HTMLElement;
  private placeholder!: Comment;
  private parentNode!: Node | null;
  private nextSibling!: Node | null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private vcr: ViewContainerRef,
    private editorService: EditorService,
    private contentManagerService: ContentManagerService,
    private cfr: ComponentFactoryResolver
  ) {
  }

  @HostListener('click', ['$event'])
  onClick(ev: MouseEvent) {
    if (
      !this.editorService.isEditModeEnabled() ||
      this.editorService.clickButton
    ) {
      return;
    }
    if (this.editorRef) {
      return;
    }

    this.editorService.closeAll();
    ev.preventDefault();
    ev.stopPropagation();
    const originalEl: HTMLElement = this.host.nativeElement;

    this.parentNode = originalEl.parentNode;
    this.nextSibling = originalEl.nextSibling;
    this.placeholder = document.createComment('editable-img');
    this.parentNode!.replaceChild(this.placeholder, originalEl);

    const factory = this.cfr.resolveComponentFactory(
      InlineImageEditorComponent
    );
    this.editorRef = this.vcr.createComponent(factory);

    // <--- KLUCZOWE!
    const elClone = this.elementFromHTML(originalEl.outerHTML);
    this.editorRef.setInput?.('value', elClone);

    if (!this.editorRef.setInput) {
      this.editorRef.instance.value = elClone;
      this.editorRef.changeDetectorRef.detectChanges();
    }

    this.editorRef.location.nativeElement.classList.add(
      ...Array.from(originalEl.classList)
    );

    this.editorRef.instance.photoOutput.subscribe((photo: UploadPhoto) => {
      this.contentManagerService.setPhoto(photo);
    });

    originalEl.style.display = 'none';

    const finish = () => {
      if (this.placeholder && this.placeholder.parentNode && originalEl) {
        try {
          this.placeholder.parentNode.insertBefore(
            originalEl,
            this.placeholder
          );
          this.placeholder.parentNode.removeChild(this.placeholder);
        } catch (error) {
          console.warn('Placeholder już nie istnieje w DOM:', error);
        }
      }

      originalEl.style.removeProperty('display');

      subSave.unsubscribe();
      subCancel.unsubscribe();
      this.removeAllToolbars();

      this.editorRef?.destroy();
      this.editorRef = null;

      this.editorService.unregister(finish);
    };

    this.editorService.register(finish);

    const subSave = this.editorRef.instance.save.subscribe((params: HTMLElementModel) => {
        // KLUCZOWE!
        // Zamieniasz string na nowy element przy każdym save
        const el = this.elementFromHTML(params.data);
        const anchor = params.anchor;
        const wrapperFactory = this.cfr.resolveComponentFactory(
          EditableImageWrapperComponent
        );
        const wrapperRef = this.vcr.createComponent(wrapperFactory);

        const outerHtml = el?.outerHTML || '';

        if (anchor) {
          // Użyj .outerHTML z klona!
          wrapperRef.instance.htmlContent = `<a href="${anchor}" target="_blank" rel="noopener noreferrer">${outerHtml}</a>`;
        } else {
          wrapperRef.instance.htmlContent = outerHtml;
        }

        this.placeholder.replaceWith(wrapperRef.location.nativeElement);
        this.appEditableImageSave.emit(wrapperRef.location.nativeElement);

        finish();
      });
    const subCancel = this.editorRef.instance.cancel.subscribe(() => finish());
  }

  removeAllToolbars() {
    document
      .querySelectorAll('.ite-toolbar')
      .forEach((el) => el.parentNode?.removeChild(el));
  }

  ngOnDestroy() {
    this.editorRef?.destroy();
  }

  private elementFromHTML(html: string): HTMLElement {
    const template = document.createElement('template');
    template.innerHTML = (html || '').trim();
    let el: HTMLElement;
    const children = Array.from(template.content.children) as HTMLElement[];
    for (const child of children) {
      if (child.nodeType !== Node.COMMENT_NODE) {
        el = child;
        break;
      }
    }
    return el as HTMLElement;
  }
}
